<template>
  <div>
    <p class="text-static-default-low mt-4 whitespace-pre-wrap">
      {{ description }}
    </p>
    <div class="mt-32">
      <FormGenerator
        v-if="id === PAYMENT_INFOS_OPTIONS.DRIVING_LICENCE_NUMBER"
        :extra-css="['!gap-28']"
        :form-config="formConfig.drivingLicense"
        :form-id
        :has-submit-button="false"
        @form-submit="handleSubmit"
      />
      <FormGenerator
        v-if="id === PAYMENT_INFOS_OPTIONS.SSN"
        :extra-css="['!gap-28']"
        :form-config="formConfig.ssn"
        :form-id
        :has-submit-button="false"
        @form-submit="handleSubmit"
      />
      <FormGenerator
        v-if="id === PAYMENT_INFOS_OPTIONS.SSN_NUMBER"
        :extra-css="['!gap-28']"
        :form-config="formConfig.fullSsn"
        :form-id
        :has-submit-button="false"
        @form-submit="handleSubmitFullSsn"
      />
      <RevInfoBlock
        v-if="!isBmAgent"
        class="mt-32 whitespace-pre-wrap"
        :content="infoBlockDescription"
        :icon="IconLockLocked"
        :title="infoBlockTitle"
        variant="default"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  PAYMENT_INFOS_OPTIONS,
  type PaymentInfosOptionsType,
} from '@backmarket/http-api/src/api-specs-buyback/customer/client'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import {
  matchingRegExp,
  maxLength,
  minLength,
  required,
} from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconLockLocked } from '@ds/icons/IconLockLocked'
import type { InputValue } from '@ds/types/InputValue'

import type {
  FormValues,
  Input,
} from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.types'
import FormGenerator from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.vue'
import translations from '@backmarket/nuxt-layer-buyback/components/IdentityInformations/IdentityInformations.translations'

const SSN_LENGTH = 4
const FULL_SSN_LENGTH = 11
const REGEX_DRIVING_LICENSE = /^[a-zA-Z0-9-]+$/

const props = withDefaults(
  defineProps<{
    formId: string
    id: PaymentInfosOptionsType
    isBmAgent?: boolean
  }>(),
  {
    isBmAgent: false,
  },
)

const i18n = useI18n()
const emit = defineEmits<{
  submit: [values: FormValues]
}>()

const formConfig: Record<string, Array<Input>> = {
  ssn: [
    {
      id: 'ssn',
      cols: 6,
      type: 'text',
      label: i18n(translations.ssnPreviewLabel),
      value: '',
      validation: [
        (accountNumber: string, values: FormValues) =>
          required(i18n(translations.required))(accountNumber, values, {}),

        (accountNumber: string, values: FormValues) =>
          maxLength(SSN_LENGTH, i18n(translations.lengthError))(
            accountNumber,
            values,
            {},
          ),

        (accountNumber: string, values: FormValues) =>
          minLength(SSN_LENGTH, i18n(translations.lengthError))(
            accountNumber,
            values,
            {},
          ),
      ],
    },
  ],
  fullSsn: [
    {
      id: 'full_social_security',
      cols: 6,
      type: 'text',
      label: i18n(translations.fullSsnPreviewLabel),
      value: '',
      validation: [
        (accountNumber: string, values: FormValues) =>
          required(i18n(translations.required))(accountNumber, values, {}),

        (accountNumber: string, values: FormValues) =>
          maxLength(FULL_SSN_LENGTH, i18n(translations.lengthErrorFullSsn))(
            accountNumber,
            values,
            {},
          ),

        (accountNumber: string, values: FormValues) =>
          minLength(FULL_SSN_LENGTH, i18n(translations.lengthErrorFullSsn))(
            accountNumber,
            values,
            {},
          ),
      ],
      formatter: (inputText: InputValue) =>
        typeof inputText === 'string'
          ? inputText
              .replace(/\D/g, '')
              .replace(/^(\d{3})(\d{2})(\d{4}).*$/, '$1-$2-$3')
          : inputText,
    },
  ],
  drivingLicense: [
    {
      id: 'drivingLicense',
      cols: 6,
      type: 'text',
      label: i18n(translations.drivingLicensePreviewLabel),
      value: '',
      validation: [
        (drivingLicense: string, values: FormValues) =>
          required(i18n(translations.required))(drivingLicense, values, {}),
        (drivingLicense: string, values: FormValues) =>
          matchingRegExp(REGEX_DRIVING_LICENSE, i18n(translations.formatError))(
            drivingLicense,
            values,
            {},
          ),
      ],
    },
  ],
}

const description = computed(() => {
  if (props.isBmAgent) {
    switch (props.id) {
      case PAYMENT_INFOS_OPTIONS.DRIVING_LICENCE_NUMBER:
        return i18n(translations.drivingLicense)
      case PAYMENT_INFOS_OPTIONS.SSN_NUMBER:
        return i18n(translations.fullSsn)
      case PAYMENT_INFOS_OPTIONS.SSN:
      default:
        return i18n(translations.ssn)
    }
  }

  switch (props.id) {
    case PAYMENT_INFOS_OPTIONS.DRIVING_LICENCE_NUMBER:
      return i18n(translations.descriptionDrivingLicense)
    case PAYMENT_INFOS_OPTIONS.SSN_NUMBER:
      return i18n(translations.descriptionFullSsn)
    case PAYMENT_INFOS_OPTIONS.SSN:
    default:
      return i18n(translations.descriptionSsn)
  }
})

const infoBlockDescription = computed(() => {
  switch (props.id) {
    case PAYMENT_INFOS_OPTIONS.DRIVING_LICENCE_NUMBER:
      return i18n(translations.infoBlockDescriptionDrivingLicense)
    case PAYMENT_INFOS_OPTIONS.SSN_NUMBER:
      return i18n(translations.infoBlockDescriptionFullSsn)
    case PAYMENT_INFOS_OPTIONS.SSN:
    default:
      return i18n(translations.infoBlockDescriptionSsn)
  }
})

const infoBlockTitle = computed(() => {
  switch (props.id) {
    case PAYMENT_INFOS_OPTIONS.DRIVING_LICENCE_NUMBER:
      return i18n(translations.infoBlockTitleDrivingLicense)
    case PAYMENT_INFOS_OPTIONS.SSN_NUMBER:
      return i18n(translations.infoBlockTitleFullSsn)
    case PAYMENT_INFOS_OPTIONS.SSN:
    default:
      return i18n(translations.infoBlockTitleSsn)
  }
})

function handleSubmit(values: FormValues) {
  emit('submit', values)
}
function handleSubmitFullSsn(values: FormValues) {
  if (typeof values.full_social_security === 'string') {
    emit('submit', {
      full_social_security: values.full_social_security.replaceAll('-', ''),
    })
  }
}
</script>
